import { Injectable, Injector } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { CareerValuation } from "@common/ADAPT.Common.Model/organisation/career-valuation";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityConfiguration } from "@common/ADAPT.Common.Model/organisation/cultural-activity-configuration";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { CulturalIndex } from "@common/ADAPT.Common.Model/organisation/cultural-index";
import { CulturalLeadership } from "@common/ADAPT.Common.Model/organisation/cultural-leadership";
import { CulturalRelationship, CulturalRelationshipBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-relationship";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { BreezePredicateUtilities } from "@common/lib/data/breeze-predicate-utilities";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { PeopleQueryUtilities } from "@common/user/people-query-utilities";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { CulturalLeadershipQueryUtilities } from "@org-common/lib/cultural-leadership/cultural-leadership-query-utilities";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { AfterOrganisationInitialisation, AfterOrganisationInitialisationObservable } from "@org-common/lib/organisation/after-organisation-initialisation.decorator";
import { BaseOrganisationService } from "@org-common/lib/organisation/base-organisation.service";
import { CareerValuationDataService } from "app/features/people/career-valuation/career-valuation-data.service";
import { CulturalIndexAuthService } from "app/features/people/cultural-index/cultural-index-auth.service";
import { CulturalIndexDataService } from "app/features/people/cultural-index/cultural-index-data.service";
import moment from "moment";
import { forkJoin, lastValueFrom } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { CareerValuationAuthService } from "../../people/career-valuation/career-valuation-auth.service";
import { ICulturalCohortMemberData } from "./cultural-cohort-member-data.interface";
import { CulturalLeadershipCatchupStatus } from "./cultural-leadership-catchup-status.enum";
import { ICulturalLeadershipCatchupStatus } from "./cultural-leadership-framework-ui.service";
import { ICulturalLeadershipPersonalAnalysisData } from "./cultural-leadership-personal-analysis-data.interface";

export interface ICulturalActivityConfigs {
    [CulturalActivityType.CareerValuation]: CulturalActivityConfiguration,
    [CulturalActivityType.CulturalIndex]: CulturalActivityConfiguration,
}

export interface ICulturalLeadershipOverdueData {
    status: CulturalLeadershipCatchupStatus;
    label: string;
    value: number;
}

export interface ICulturalLeadershipPersonalAnalysisBlock {
    people?: Person[];
    culturalIndexes?: CulturalIndex[];
    careerValuations?: CareerValuation[];
    culturalRelationships?: CulturalRelationship[];
    culturalActivities?: CulturalActivity[];
    culturalActivityConfigs?: ICulturalActivityConfigs;
}

@Injectable({
    providedIn: "root",
})
export class CulturalLeadershipFrameworkService extends BaseOrganisationService {
    private configuration!: CulturalLeadership;
    private clQueryUtilities = new CulturalLeadershipQueryUtilities(this.commonDataService);
    private catchupStatuses: ICulturalLeadershipCatchupStatus[];
    private culturalActivityConfigs?: ICulturalActivityConfigs;

    public constructor(
        injector: Injector,
        private authService: AuthorisationService,
        private featuresService: FeaturesService,
        private ciDataService: CulturalIndexDataService,
        private cvDataService: CareerValuationDataService,
        private culturalActivityService: CulturalActivityService,
    ) {
        super(injector);

        this.catchupStatuses = [
            {
                id: CulturalLeadershipCatchupStatus.Overdue,
                defaultLabel: "Overdue",
                class: "catchup-status-overdue",
                badgeClass: "badge-overdue",
            },
            {
                id: CulturalLeadershipCatchupStatus.Due,
                defaultLabel: "Due within a month",
                class: "catchup-status-due",
                badgeClass: "badge-due",
            },
            {
                id: CulturalLeadershipCatchupStatus.OnTime,
                defaultLabel: "On schedule",
                class: "catchup-status-on-schedule",
                badgeClass: "badge-on-schedule",
            },
            {
                id: CulturalLeadershipCatchupStatus.Unscheduled,
                defaultLabel: "Unscheduled",
                class: "catchup-status-unscheduled",
                badgeClass: "badge-unscheduled",
            },
        ];
    }

    protected organisationInitialisationActions() {
        return [this.initialiseService()];
    }

    @Autobind
    protected async initialiseService() {
        this.configuration = await this.clQueryUtilities.promiseToGetCulturalLeadershipConfiguration();

        const obs = forkJoin([
            this.culturalActivityService.getCulturalActivityConfiguration(CulturalActivityType.CulturalIndex),
            this.culturalActivityService.getCulturalActivityConfiguration(CulturalActivityType.CareerValuation),
        ]).pipe(
            map(([ciConfig, cvConfig]) => ({
                [CulturalActivityType.CulturalIndex]: ciConfig,
                [CulturalActivityType.CareerValuation]: cvConfig,
            } as ICulturalActivityConfigs)),
        );

        this.culturalActivityConfigs = await lastValueFrom(obs);
    }

    public async promiseToGetLatestCulturalFrameworkData() {
        const unorderedFrame: ICulturalLeadershipPersonalAnalysisBlock = {};

        unorderedFrame.people = await new PeopleQueryUtilities(this.commonDataService).promiseToGetActivePeople();

        const culturalIndexEnabled = await this.featuresService.promiseToCheckIfFeatureActive(FeatureName.CulturalNetworkCulturalIndex);
        if (culturalIndexEnabled) {
            await this.authService.promiseToVerifyAccess(CulturalIndexAuthService.ReadAnonymousCulturalIndex);
            unorderedFrame.culturalIndexes = await this.ciDataService.promiseToGetLatestCulturalIndexes();
        }

        const careerValuationEnabled = await this.featuresService.promiseToCheckIfFeatureActive(FeatureName.CulturalNetworkCareerValuation);
        if (careerValuationEnabled) {
            await this.authService.promiseToVerifyAccess(CareerValuationAuthService.ReadAnonymousCareerValuation);
            unorderedFrame.careerValuations = await this.cvDataService.promiseToGetLatestCareerValuations();
        }

        unorderedFrame.culturalRelationships = await this.clQueryUtilities.promiseToGetActiveCulturalLeadershipRelationships();
        unorderedFrame.culturalActivityConfigs = await this.promiseToGetCulturalActivityConfigurations();

        unorderedFrame.culturalActivities = await lastValueFrom(this.culturalActivityService.getCulturalActivitiesForActivePeople([CulturalActivityType.CareerValuation, CulturalActivityType.CulturalIndex]));
        unorderedFrame.culturalActivities = unorderedFrame.culturalActivities.filter((activity) =>
            activity.person1?.getLatestEmployeeConnection()?.isActive() || activity.person2?.getLatestEmployeeConnection()?.isActive());

        return this.prepareFrameworkData(unorderedFrame);
    }

    @Autobind
    private prepareFrameworkData(referenceData: ICulturalLeadershipPersonalAnalysisBlock) {
        const allPeople: Person[] = referenceData.people!;
        const allPersonalCulturalData = allPeople
            .filter((person: Person) => {
                const connection = person.getLatestEmployeeConnection();
                return connection && connection.isActive();
            })
            .map((person: Person) => ({ person })) as ICulturalLeadershipPersonalAnalysisData[];

        const eventData = referenceData.culturalActivities ?? [];

        const culturalIndexData = referenceData.culturalIndexes ?? [];
        const cis: CulturalIndex[] = [];
        culturalIndexData.forEach((culturalIndex) => cis[culturalIndex.personId] = culturalIndex);
        allPersonalCulturalData.forEach((personalCulturalData) => {
            personalCulturalData.culturalIndex = cis[personalCulturalData.person.personId];
        });

        const careerValuation = referenceData.careerValuations ?? [];
        const cvs: CareerValuation[] = [];
        careerValuation.forEach((cv) => cvs[cv.personId] = cv);
        allPersonalCulturalData.forEach((personalCulturalData) => {
            personalCulturalData.careerValuation = cvs[personalCulturalData.person.personId];
        });

        allPersonalCulturalData.forEach((personalCulturalData) => {
            this.calculateNextCatchupDate(personalCulturalData, eventData.filter((event) => event.person1Id === personalCulturalData.person.personId));
        });

        /*The grid binds to only one cultural leader per person.
        Check for any people with multiple culturalRelationships.
        If so, duplicate the person and assign a distinct cultural leader.*/
        const people = referenceData.culturalRelationships ?? [];
        allPersonalCulturalData.forEach((personalCulturalData) => {
            const relationships = people.filter((p) => p.personId === personalCulturalData.person.personId);
            relationships.forEach((relationship, i) => {
                if (i === 0) {
                    personalCulturalData.culturalLeader = relationship.culturalLeader;
                    personalCulturalData.isSupporting = relationship.isSupporting;
                } else {
                    allPersonalCulturalData.push({
                        ...personalCulturalData,
                        culturalLeader: relationship.culturalLeader,
                        isSupporting: relationship.isSupporting,
                    });
                }
            });
        });

        return allPersonalCulturalData;
    }

    private calculateNextCatchupDate(personalCulturalData: ICulturalLeadershipPersonalAnalysisData, events: CulturalActivity[]) {
        // relationships not needed for this
        const data = this.getIndividualCohortMemberStatus(events, undefined, undefined, personalCulturalData.careerValuation, personalCulturalData.culturalIndex);

        if (data.latestCatchup) {
            personalCulturalData.latestCatchup = data.latestCatchup;
        }

        personalCulturalData.nextCatchup = data.nextCatchup;
        personalCulturalData.nextCatchupActivity = data.nextCatchupActivity;
        personalCulturalData.nextCatchupType = data.nextCatchupType;

        personalCulturalData.catchupStatus = !personalCulturalData.latestCatchup
            ? CulturalLeadershipCatchupStatus.Unscheduled
            : data.catchupStatus.id;
    }

    public async promiseToAddCulturalRelationship(culturalLeader: Person): Promise<CulturalRelationship> {
        const defaults = {
            organisation: this.configuration.organisation,
            culturalLeader,
            startDate: moment.utc().toDate(),
        };

        return await lastValueFrom(this.commonDataService.create(CulturalRelationshipBreezeModel, defaults));
    }

    public promiseToRemoveCulturalRelationship(culturalRelationship: CulturalRelationship) {
        if (culturalRelationship.entityAspect.entityState.isAdded()) {
            return lastValueFrom(this.commonDataService.remove(culturalRelationship));
        }

        culturalRelationship.endDate = moment.utc().toDate();

        return Promise.resolve(undefined);
    }

    @AfterOrganisationInitialisation
    public async promiseToGetCulturalLeadershipConfiguration() { // leave async here even if there is no away as the function becomes a promise from the decorator
        return this.configuration;
    }

    @AfterOrganisationInitialisation
    public async promiseToGetCulturalActivityConfigurations() { // leave async here even if there is no away as the function becomes a promise from the decorator
        return this.culturalActivityConfigs;
    }

    @AfterOrganisationInitialisationObservable
    public getCulturalCohort(personId?: number) {
        const predicate = new MethodologyPredicate<CulturalRelationship>("culturalLeaderId", "==", personId)
            .and(BreezePredicateUtilities.getIsActivePredicate());

        return this.commonDataService.getByPredicate(CulturalRelationshipBreezeModel, predicate);
    }

    public getCulturalCohortMemberData(personId: number) {
        return this.getCulturalCohort().pipe(
            switchMap((culturalRelationships) => {
                return culturalRelationships?.length
                    ? this.getCohortMemberStatus(culturalRelationships, personId)
                    : [];
            }),
            map((data) => data.sort((a, b) => a.relationship.person.fullName.localeCompare(b.relationship.person.fullName))),
        );
    }

    private async getCohortMemberStatus(relationships: CulturalRelationship[], personId: number) {
        const careerValuationIsEnabled = this.featuresService.isFeatureActive(FeatureName.CulturalNetworkCareerValuation);
        const culturalIndexIsEnabled = this.featuresService.isFeatureActive(FeatureName.CulturalNetworkCulturalIndex);

        const cohortMemberData: ICulturalCohortMemberData[] = [];
        const filteredRelationships = relationships.filter((r) => r.culturalLeaderId === personId);
        const cohortPersonIds = filteredRelationships.map((fr) => fr.personId);
        const careerValuations = careerValuationIsEnabled
            ? await this.cvDataService.promiseToGetLatestCareerValuationsForPersonIds(cohortPersonIds)
            : [];
        const culturalIndexes = culturalIndexIsEnabled
            ? await this.ciDataService.promiseToGetLatestCulturalIndexesForPersonIds(cohortPersonIds)
            : [];

        // can't use getUpcomingCulturalActivitiesForCulturalLeader as will not include supporting people
        const cadenceEvents = await lastValueFrom(this.culturalActivityService.getCulturalActivitiesForActivePeople([CulturalActivityType.CareerValuation, CulturalActivityType.CulturalIndex]));

        // fetch the supp data now so we can show the reschedule comment
        const toFetchSuppData = cadenceEvents
            .filter((e) => !e.isCompleted && e.extensions.hasRescheduled && !e.supplementaryData)
            .map((e) => e.culturalActivityId);
        await lastValueFrom(this.culturalActivityService.getCulturalActivitySupplementaryDataForActivityIds(toFetchSuppData));

        filteredRelationships.forEach((relationship) => {
            const personEvents = cadenceEvents.filter((event) => event.person1Id === relationship.personId);
            const primaryCulturalRelationship = relationships.find((r) => r.personId === relationship.personId && !r.isSupporting);
            const latestCvt = careerValuations.find((cvt) => cvt.personId === relationship.personId);
            const latestCi = culturalIndexes.find((ci) => ci.personId === relationship.personId);
            cohortMemberData.push(this.getIndividualCohortMemberStatus(personEvents, relationship, primaryCulturalRelationship, latestCvt, latestCi));
        });

        return cohortMemberData;
    }

    private getIndividualCohortMemberStatus(culturalActivities: CulturalActivity[], relationship?: CulturalRelationship, primaryCulturalRelationship?: CulturalRelationship, latestCvt?: CareerValuation, latestCi?: CulturalIndex) {
        const careerValuationIsEnabled = this.featuresService.isFeatureActive(FeatureName.CulturalNetworkCareerValuation);
        const culturalIndexIsEnabled = this.featuresService.isFeatureActive(FeatureName.CulturalNetworkCulturalIndex);

        let latestActivity: CulturalActivity | undefined = undefined;
        let latestCVTActivity: CulturalActivity | undefined = undefined;
        let latestCIActivity: CulturalActivity | undefined = undefined;
        let nextCatchupActivity: CulturalActivity | undefined = undefined;
        let nextCVTActivity: CulturalActivity | undefined = undefined;
        let nextCIActivity: CulturalActivity | undefined = undefined;

        for (const activity of culturalActivities) {
            if (activity.isCompleted) {
                latestActivity = compareMaxEvent(latestActivity, activity);
                if (activity.type == CulturalActivityType.CareerValuation) {
                    latestCVTActivity = compareMaxEvent(latestCVTActivity, activity);
                } else if (activity.type == CulturalActivityType.CulturalIndex) {
                    latestCIActivity = compareMaxEvent(latestCIActivity, activity);
                }
            } else {
                nextCatchupActivity = compareMinEvent(nextCatchupActivity, activity);
                if (activity.type == CulturalActivityType.CareerValuation) {
                    nextCVTActivity = compareMinEvent(nextCVTActivity, activity);
                } else if (activity.type == CulturalActivityType.CulturalIndex) {
                    nextCIActivity = compareMinEvent(nextCIActivity, activity);
                }
            }
        }

        const latestDates = [latestCVTActivity?.date, latestCIActivity?.date, latestCvt?.creationDate, latestCi?.creationDate]
            .filter((date) => !!date);
        const latestCatchup = latestDates.length > 0
            ? moment.max(latestDates.map((d) => moment(d))).toDate()
            : undefined;

        // use the date from the latest entity always.
        // can be the case where you have a CVT/CI entity but no CVT/CI activity,
        // say if you record one without a cultural leader
        const targetCvtDate = latestCVTActivity && latestCvt
            ? moment.max(moment(latestCVTActivity.date), moment(latestCvt.creationDate)).toDate()
            : latestCVTActivity?.date ?? latestCvt?.creationDate ?? latestCatchup;
        const targetCiDate = latestCIActivity && latestCi
            ? moment.max(moment(latestCIActivity.date), moment(latestCi.creationDate)).toDate()
            : latestCIActivity?.date ?? latestCi?.creationDate ?? latestCatchup;

        // use the date from the latest event, else calculate the next date based on the cadence.
        // there normally should be an event to use
        const nextCVT = careerValuationIsEnabled
            ? nextCVTActivity?.date ?? this.getNextCatchupDate(targetCvtDate, CulturalActivityType.CareerValuation)
            : undefined;
        const nextCI = culturalIndexIsEnabled
            ? nextCIActivity?.date ?? this.getNextCatchupDate(targetCiDate, CulturalActivityType.CulturalIndex)
            : undefined;

        // if there are no CVT/CI events, then don't have a due date for the next catchup
        let nextCatchup: Date | undefined;
        let nextCatchupType: CulturalActivityType | undefined;

        if (nextCVT && nextCI) {
            const ciFirst = nextCI.getTime() < nextCVT.getTime();
            nextCatchup = ciFirst ? nextCI : nextCVT;
            nextCatchupType = ciFirst ? CulturalActivityType.CulturalIndex : CulturalActivityType.CareerValuation;
        } else if (nextCVT) {
            nextCatchup = nextCVT;
            nextCatchupType = CulturalActivityType.CareerValuation;
        } else if (nextCI) {
            nextCatchup = nextCI;
            nextCatchupType = CulturalActivityType.CulturalIndex;
        }

        const today = moment().startOf("day").toDate();
        const catchupStatus = this.getCatchupStatus(this.getCatchupStatusFromDate(today, nextCatchup));

        return {
            relationship,
            latestCatchup,
            nextCatchup,
            nextCatchupType,
            nextCVT,
            nextCI,
            nextCVTActivity,
            nextCIActivity,
            nextCatchupActivity,
            catchupStatus,
            primaryCulturalLeaderId: primaryCulturalRelationship?.culturalLeaderId,
        } as ICulturalCohortMemberData;

        function compareMaxEvent(currentMax: CulturalActivity | undefined, event: CulturalActivity) {
            return currentMax
                ? moment(event.date).isAfter(currentMax.date) ? event : currentMax
                : event;
        }

        function compareMinEvent(currentMin: CulturalActivity | undefined, event: CulturalActivity) {
            return currentMin
                ? moment(event.date).isBefore(currentMin.date) ? event : currentMin
                : event;
        }
    }

    public getCatchupStatuses() {
        return this.catchupStatuses;
    }

    public getCatchupStatus(statusId: CulturalLeadershipCatchupStatus) {
        return this.catchupStatuses.find((element) => element.id === statusId);
    }

    public getCatchupStatusFromDate(currentDate: Date, nextCatchupDate?: Date) {
        if (!nextCatchupDate) {
            return CulturalLeadershipCatchupStatus.Unscheduled;
        } else if (moment(nextCatchupDate).isBefore(currentDate)) {
            return CulturalLeadershipCatchupStatus.Overdue;
        } else {
            const monthFromCurrentDate = moment(currentDate)
                .add(1, "month")
                .toDate();

            if (moment(nextCatchupDate).isBefore(monthFromCurrentDate)) {
                return CulturalLeadershipCatchupStatus.Due;
            } else {
                return CulturalLeadershipCatchupStatus.OnTime;
            }
        }
    }

    public getNextCatchupDate(date: Date | undefined, type: CulturalActivityType.CareerValuation | CulturalActivityType.CulturalIndex) {
        if (!date) {
            return undefined;
        }

        // no cadence, so no next date
        const config = this.culturalActivityConfigs?.[type];
        if (config?.frequencyMonths === 0) {
            return undefined;
        }

        return moment(date)
            .add(config?.frequencyMonths ?? 0, "months")
            .toDate();
    }
}
